import { CategoryBadge } from '@components/CategoryBadge';
import { FavoriteIcon } from '@components/Icons/FavoriteIcon';
import { Likedicon } from '@components/Icons/LikedIcon';
import LazyVideo from '@components/LazyVideo';
import { useLowDataMode } from '@hooks/station';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { placeHolderData } from '../../config';

interface Contributor {
  id: string;
  givenName: string;
  thumbnailUrl?: string;
  videoMobileUrl?: string;
}

interface SerenityCardProps {
  album: any;
  contributor?: Contributor;
  videoUrl?: string;
  posterUrl?: string;
  albumName?: string;
  likes?: number;
  isLiked?: boolean;
  onClick?: (e?: React.MouseEvent) => void;
  onFavoriteClick?: (e: React.MouseEvent) => void;
  showLikesCount?: boolean;
  compactSize?: boolean;
}

export const SerenityCard = memo(
  ({
    album,
    contributor,
    videoUrl,
    posterUrl,
    albumName,
    likes,
    isLiked,
    onClick,
    onFavoriteClick,
    showLikesCount = true,
    compactSize = false,
  }: SerenityCardProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isLowDataMode] = useLowDataMode();
    const displayPosterUrl = posterUrl || contributor?.thumbnailUrl || '';
    const displayVideoUrl =
      videoUrl ||
      contributor?.videoMobileUrl ||
      (displayPosterUrl ? '' : placeHolderData.contributorMobileVideo);
    const displayAlbumName = albumName || album?.name || t('common.unknownAlbum');
    const displayContributorName = contributor?.givenName || t('common.unknownArtist');
    const backgroundImageUrl = displayPosterUrl || placeHolderData.albumCover;

    const handleContributorClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (contributor?.id) {
        navigate(`/contributor/${contributor.id}`);
      }
    };

    return (
      <div
        className={`w-full snap-start relative rounded-[16px] shadow-[0_4px_16px_0_rgba(0,0,0,0.40)] cursor-pointer hover:shadow-[0_4px_20px_0_rgba(0,0,0,0.50)] transition-all duration-300 opacity-100 ${
          compactSize ? 'h-[395px]' : 'h-[520px]'
        }`}
        onClick={() => onClick?.()}>
        {/* Background Video */}
        <div className="flex absolute top-0 left-0 w-full h-full rounded-[16px] overflow-hidden">
          {isLowDataMode ? (
            <img
              className="w-full h-full object-cover"
              src={backgroundImageUrl}
              alt=""
            />
          ) : (
            <LazyVideo
              className="w-full h-full object-cover"
              src={displayVideoUrl}
              poster={displayPosterUrl || undefined}
              autoPlay
              loop
              muted
              playsInline
              webkit-playsinline="true"
              preload="metadata"
              enabled
              rootMargin="400px"
              threshold={0.1}
            />
          )}
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_34.69%,rgba(0,0,0,0.9)_100%)] z-[99] rounded-[16px]" />

        {/* Serenity Badge */}
        <CategoryBadge category="serenity" />

        {/* Bottom Section */}
        <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-[7px] w-full text-left">
          <div className="text-white line-clamp-2 font-[DM_Serif_Display] font-normal text-[20px] md:text-[24px] leading-[28px]">
            {displayAlbumName}
          </div>

          <div className="flex items-center justify-between gap-3">
            <div
              className="flex items-center gap-[16px] flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleContributorClick}>
              <img
                src={
                  contributor?.thumbnailUrl ||
                  placeHolderData.contributorThumbnail
                }
                alt={displayContributorName}
                className="w-[56px] h-[56px] rounded-full flex-shrink-0 object-cover"
              />
              <span className="text-white font-[DM_Sans] font-[400] text-[14px] md:text-[16px]">
                {displayContributorName}
              </span>
            </div>
            {showLikesCount && likes !== undefined && onFavoriteClick && (
              <div
                className="flex items-center gap-1 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={onFavoriteClick}>
                {/* <span className="text-white/80 text-[12px] font-semibold leading-[130%]">
                  {likes}
                </span> */}
                {isLiked ? (
                  <Likedicon width={20} height={20} />
                ) : (
                  <FavoriteIcon width={20} height={20} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

SerenityCard.displayName = 'SerenityCard';
